import { createContext, useContext, useEffect, useReducer, useRef, useState } from "react"
import {
  ActivitiesByIdsPayload,
  activitiesStatus,
  ActivitySection,
  DetailInformPayload,
  initialState,
  LogbookActionEnum,
  LogbookContextType,
  LogbookLoadings,
  SearchUserPayload,
  TabsItem,
  useDestinationsFilter,
  useListTabDefault,
  useListUserFilter,
  useOneUserFilter,
  UserMapListPayload,
  useUserFilter,
} from "../utils"
import { reducer } from "../utils/reducers"
import {
  getArea,
  getDepartments,
  getDivisions,
  getRoles,
  getStore,
  getUsers,
  getUserTypes,
} from "../../../../../utils/methods/geServiceWithItemsFormat"
import { Item, ObjectGeneric } from "../../../../../utils/types/generals"
import { loogbookServiceFetch, userServiceFetch } from "../../../../../utils/constants/callService"
import { SelectUserPayload, UserItem } from "../components/shared/formActivity/selectUsersToAssign/types"
import { statusService } from "../../../../../service"
import { useLocation, useNavigate } from "react-router-dom"
import { getPreviousYears, monthYear } from "../../../../../utils/constants/date_month"
import { UserMarkMap } from "../components/shared/map/types"
import { CodeDetasilInbform, UrlDetasilInbform } from "../../../../../service/logbook/response"
import { downloadBlob } from "../../../../../utils/methods/downloadFiles"
import { getUserRole } from "../../../../../service/auth"
import { Roles } from "../../../../../utils/enums"
import { UserSection } from "../utils/enums/userSection"

type Prefilters = {
  userId?: string
  roleId?: string
  storeId?: string
  assignedQuestionnaireIds?: string
  userQuestionnaireIds?: string
}

type ScheduleMeta = {
  origin?: "USER" | "UNIT"
  scheduleId?: string | number
  questionnaireName?: string
  assignedQuestionnaireIds?: string
  userQuestionnaireIds?: string
}

export const LogbookContext = createContext<LogbookContextType>(Object({}))
export const useLogbookContext = () => useContext(LogbookContext)

export const LoogbookContextProvider = ({ children }: { children: JSX.Element }) => {
  const navigate = useNavigate()
  const location = useLocation()

  const listTab = useListTabDefault()
  const [state, dispatch] = useReducer(reducer, initialState)

  const [visibleUsersData, setVisibleUsersData] = useState<Record<string, UserMarkMap>>({})
  const [visibleUsersDataGeneralList, setVisibleUsersDataGeneralList] = useState<Record<string, UserMarkMap>>({})
  const [visibleUsersDataByUser, setVisibleUsersDataByUser] = useState<Record<string, UserMarkMap>>({})
  const [hiddenLocalConstant, setHiddenLocalConstant] = useState<string[]>([])
  const [items, setItems] = useState<Record<string, Item[]>>()

  const filterUser = useUserFilter({ items })
  const filterOneUser = useOneUserFilter({ items })
  const filterMyActivity = useOneUserFilter({ items })
  const filterListUser = useListUserFilter({ items })
  const filterDestinations = useDestinationsFilter({ items })

  const [pendingPrefilters, setPendingPrefilters] = useState<Prefilters | null>(() => {
    const st = (location.state as any) ?? null
    return (st?.preFilters as Prefilters) ?? null
  })

  const [scheduleMeta, setScheduleMeta] = useState<ScheduleMeta | null>(() => {
    const st = (location.state as any) ?? null
    return (st?.scheduleMeta as ScheduleMeta) ?? null
  })

  const [forceMyActivityTab, setForceMyActivityTab] = useState<boolean>(() => {
    const st = (location.state as any) ?? null
    return Boolean(st?.forceMyActivityTab)
  })

  const didInitTabRef = useRef(false)

  const [forcedAssignedQuestionnaireIds, setForcedAssignedQuestionnaireIds] = useState<string | undefined>(() => {
    const st = (location.state as any) ?? null
    return st?.preFilters?.assignedQuestionnaireIds
  })

  const [forcedUserQuestionnaireIds, setForcedUserQuestionnaireIds] = useState<string | undefined>(() => {
    const st = (location.state as any) ?? null
    return st?.preFilters?.userQuestionnaireIds
  })

  useEffect(() => {
    const st = (location.state as any) ?? null
    if (st?.preFilters) setPendingPrefilters(st.preFilters as Prefilters)
    if (st?.scheduleMeta) setScheduleMeta(st.scheduleMeta as ScheduleMeta)
    if (st?.forceMyActivityTab) setForceMyActivityTab(true)
  }, [location.state])

  const changeTab = ({ value }: { value: TabsItem }) => {
    dispatch({ type: LogbookActionEnum.CHANGE_TAB, payload: { value } })
    dispatch({ type: LogbookActionEnum.ACTIVITY_SECTION, payload: { value: ActivitySection.USER_LIST } })

    if (window.location.href.includes("?") || window.location.href.includes("#")) {
      navigate(window.location.pathname, { replace: true })
    }
  }

  const changeActivitySection = ({ value }: { value: ActivitySection }) => {
    dispatch({ type: LogbookActionEnum.ACTIVITY_SECTION, payload: { value } })
  }

  const selectUserToActivities = ({ id }: { id: string }) => {
    void id
  }

  const getStatusItem = async () => {
    if (items?.status?.length) return
    const item = (await statusService().statusList()).filter((status) => activitiesStatus.includes(status.id))
    setItems((prev) => ({
      ...prev,
      status: item.map((it) => ({ value: it.id, id: it.id.toString(), label: it.description })),
    }))
  }

  const getStoreItem = async () => {
    if (items?.stores?.length) return
    const { items: item } = await getStore()
    setItems((prev) => ({ ...prev, stores: item }))
  }

  const getRoleItem = async () => {
    if (items?.role?.length) return
    const { items: item } = await getRoles({ skip: [Roles.ADMIN] })
    setItems((prev) => ({ ...prev, role: item }))
  }

  const getDivisionItem = async () => {
    if (items?.division?.length) return
    const { items: item } = await getDivisions()
    setItems((prev) => ({ ...prev, division: item }))
  }

  const getUserTypeItem = async () => {
    if (items?.userType?.length) return
    const { items: item } = await getUserTypes()
    setItems((prev) => ({ ...prev, userType: item }))
  }

  const getUsersItem = async () => {
    if (items?.user?.length) return
    const { items: item } = await getUsers({ addUserLogged: true })
    setItems((prev) => ({ ...prev, user: item }))
  }

  const getAreaItem = async () => {
    if (items?.area?.length) return
    const item = await getArea()
    setItems((prev) => ({ ...prev, area: item }))
  }

  const getDepartmentItem = async () => {
    if (items?.department?.length) return
    const item = await getDepartments()
    setItems((prev) => ({ ...prev, department: item }))
  }

  const searchUsersToAssign = async ({ query }: SearchUserPayload) => {
    const response = await userServiceFetch.filterUsers({ query: `status_ids=1&${query}` })
    const list: UserItem[] = response.map((user) => ({
      id: user.id.toString(),
      fullName: user.fullName,
      role: user.role,
    }))
    dispatch({ type: LogbookActionEnum.SEARCH_USERS, payload: { value: list } })
  }

  const datesItems = () => {
    setItems((prev) => ({
      ...prev,
      year: getPreviousYears(),
      month: monthYear.map((it) => ({ ...it, value: it.value + 1 })),
    }))
  }

  const selectUser = (payload: SelectUserPayload) => {
    dispatch({ type: LogbookActionEnum.SELECT_USER_TO_ASSIGN, payload })
  }

  const loadingUserList = (value: boolean = true) => {
    dispatch({ type: LogbookActionEnum.LOADINGS, payload: { name: LogbookLoadings.USER_LIST, value } })
  }

  const loadingActivities = (value: boolean = true) => {
    dispatch({ type: LogbookActionEnum.LOADINGS, payload: { name: LogbookLoadings.GET_ACTIVITIES, value } })
  }

  const normalizeQuestionnaireQuery = (q: string) => {
    if (!q) return q
    return q.replace(/userqestionnaire_ids=/g, "userquestionnaire_ids=")
  }

  const withAssignedQuestionnaire = (q: string) => {
    const normalized = normalizeQuestionnaireQuery(q || "")

    if (normalized.includes("assignedquestionnaire_ids=")) return normalized
    if (normalized.includes("userquestionnaire_ids=")) return normalized

    if (forcedUserQuestionnaireIds) {
      return normalized
        ? `${normalized}&userquestionnaire_ids=${forcedUserQuestionnaireIds}`
        : `userquestionnaire_ids=${forcedUserQuestionnaireIds}`
    }

    if (forcedAssignedQuestionnaireIds) {
      return normalized
        ? `${normalized}&assignedquestionnaire_ids=${forcedAssignedQuestionnaireIds}`
        : `assignedquestionnaire_ids=${forcedAssignedQuestionnaireIds}`
    }

    return normalized
  }

  const getActivities = async ({ ids, myActivity, onlyUser, query }: ActivitiesByIdsPayload) => {
    const finalQuery = withAssignedQuestionnaire(query || "")

    const parts: string[] = []
    if (!myActivity) parts.push(`ids_user=${ids.join(",")}`)
    if (finalQuery) parts.push(finalQuery)

    const response = await loogbookServiceFetch.listOfActiviesOfId({
      query: parts.join("&"),
      onlyUser,
      myActivity,
    })

    if (myActivity) {
      dispatch({
        type: LogbookActionEnum.SAVE_USER_ACTIVITIES_MARK,
        payload: { value: response.transform.userMark, myActivity, totalPage: response.total },
      })
    }

    loadingActivities(false)
    return response
  }

  const getMyTypes = async ({ id, query }: { id: string; query: string }) => {
    return loogbookServiceFetch.listOfMyTypes({ query, id })
  }

  const getDetailInform = async ({ query, uuidTab }: DetailInformPayload) => {
    const response = await loogbookServiceFetch.getDetailInform({ uuid: state.uuid[uuidTab], query })

    if ((response as CodeDetasilInbform).code !== "02") {
      dispatch({ type: LogbookActionEnum.EDIT_ERROR, payload: { error: false, uuidType: uuidTab } })

      if (!state.uuid[uuidTab]) {
        dispatch({ type: LogbookActionEnum.EDIT_UUID, payload: { uuid: (response as any).uuid, uuidType: uuidTab } })
        dispatch({ type: LogbookActionEnum.LOAD_STATE, payload: { load: true, loadType: uuidTab } })
      } else {
        const url = (response as UrlDetasilInbform).reportUrl ?? ""
        if (url) {
          const res = await fetch(url)
          if (!res.ok) throw new Error(`Error al descargar el archivo: ${res.statusText}`)
          const blob = await res.blob()
          const zipBlob = new Blob([blob], { type: "application/zip" })
          downloadBlob(zipBlob, "Informe resumen")
          dispatch({ type: LogbookActionEnum.EDIT_UUID, payload: { uuid: undefined, uuidType: uuidTab } })
          dispatch({ type: LogbookActionEnum.LOAD_STATE, payload: { load: false, loadType: uuidTab } })
        }
      }
    } else {
      dispatch({ type: LogbookActionEnum.EDIT_ERROR, payload: { error: true, uuidType: uuidTab } })
      dispatch({ type: LogbookActionEnum.LOAD_STATE, payload: { load: false, loadType: uuidTab } })
    }
  }

  const change = ({ uuidTab }: DetailInformPayload) => {
    dispatch({ type: LogbookActionEnum.EDIT_ERROR, payload: { error: false, uuidType: uuidTab } })
  }

  const deleteActivity = async ({ id }: { id: string }) => {
    return loogbookServiceFetch.deleteActivities(id)
  }

  const usersMarkList = async ({ query }: UserMapListPayload) => {
    const finalQuery = withAssignedQuestionnaire(query || "")

    const response = await loogbookServiceFetch.usersActivities({
      query: `view=users${finalQuery ? `&${finalQuery}` : ""}`,
    })

    dispatch({
      type: LogbookActionEnum.SAVE_USER_LIST_MAP,
      payload: {
        userList: response.transform.userList,
        usersMark: response.transform.userMark || [],
        totalPage: response.total ?? 0,
      },
    })
    loadingUserList(false)
    return { users: response.transform.users }
  }

  const checkUser = ({ ids, all }: { ids: string[]; all?: boolean }) => {
    dispatch({ type: LogbookActionEnum.CHECK_USER, payload: { ids, all } })
  }

  const getDestinations = async ({ query }: { query: string }) => {
    const response = await loogbookServiceFetch.listOfDestinations({ query: `${query ? `&${query}` : ""}` })
    return response
  }

  const deleteDestination = async ({ id }: { id: number }) => {
    return loogbookServiceFetch.deleteDestination(id)
  }

  const updateDestination = async ({ id, body }: { body: ObjectGeneric; id: number }) => {
    return loogbookServiceFetch.updateDestination(body, id)
  }

  const convertInUnity = async ({ id, body }: { body: ObjectGeneric; id: number }) => {
    return loogbookServiceFetch.convertToUnity(body, id)
  }

  const getReportDestination = async ({ query, type }: { query: string; type: string }) => {
    const response = await loogbookServiceFetch.getReportDestination({ query, type })
    if (!response.errors) {
      dispatch({
        type: LogbookActionEnum.EDIT_UUID,
        payload: { uuid: response.uuid, uuidType: UserSection.DESTINATION },
      })
      dispatch({
        type: LogbookActionEnum.LOAD_STATE,
        payload: { load: true, loadType: UserSection.DESTINATION },
      })
    }
  }

  const getByUuidDestination = async ({ uuid }: { uuid: string }) => {
    const response = await loogbookServiceFetch.getByUuidDestination({ uuid })
    const url = (response as UrlDetasilInbform).reportUrl ?? ""
    if (url) {
      const path = url.split("?")[0]
      const parts = path.split(".")
      const extension = parts[parts.length - 1] || "zip"
      const res = await fetch(url)
      if (!res.ok) throw new Error(`Error al descargar el archivo: ${res.statusText}`)
      const blob = await res.blob()
      downloadBlob(blob, `Destinos.${extension}`)
      dispatch({ type: LogbookActionEnum.EDIT_UUID, payload: { uuid: undefined, uuidType: UserSection.DESTINATION } })
      dispatch({ type: LogbookActionEnum.LOAD_STATE, payload: { load: false, loadType: UserSection.DESTINATION } })
    }
  }

  useEffect(() => {
    if (forceMyActivityTab) {
      dispatch({ type: LogbookActionEnum.CHANGE_TAB, payload: { value: TabsItem.MY_ACTIVITY } })
      didInitTabRef.current = true
      return
    }

    if (didInitTabRef.current) return

    const newRole = getUserRole()
    const desiredTab = newRole === Roles.PARTNER ? TabsItem.MY_ACTIVITY : TabsItem.ACTIVITY_REGISTER

    dispatch({ type: LogbookActionEnum.CHANGE_TAB, payload: { value: desiredTab } })
    didInitTabRef.current = true
  }, [forceMyActivityTab])

  useEffect(() => {
    if (!pendingPrefilters) return

    setForcedAssignedQuestionnaireIds(pendingPrefilters.assignedQuestionnaireIds)
    setForcedUserQuestionnaireIds(pendingPrefilters.userQuestionnaireIds)

    const desiredTab = forceMyActivityTab ? TabsItem.MY_ACTIVITY : TabsItem.ACTIVITY_REGISTER
    dispatch({ type: LogbookActionEnum.CHANGE_TAB, payload: { value: desiredTab } })

    if (!forceMyActivityTab) {
      const storesReady = !!items?.stores?.length
      if (!storesReady) return

      const pickItem = (list: Item[] | undefined, id: string): Item => {
        const found = list?.find((it) => it.id === id) ?? list?.find((it) => String(it.value) === id)
        const base: Item = found ?? { id, value: Number(id), label: id }
        return { ...base, id, value: Number(id) }
      }

      dispatch({ type: LogbookActionEnum.ACTIVITY_SECTION, payload: { value: ActivitySection.USER_LIST } })

      if (pendingPrefilters.storeId) {
        const storeItem = pickItem(items?.stores, pendingPrefilters.storeId)
        filterUser.filter.onChange({ target: { name: "store_activity_ids", value: [storeItem] } } as any)
      }
    }

    navigate(location.pathname + location.search + location.hash, { replace: true, state: null })
    setPendingPrefilters(null)
    setForceMyActivityTab(false)
  }, [
    pendingPrefilters,
    items?.stores,
    filterUser.filter,
    location.pathname,
    location.search,
    location.hash,
    navigate,
    forceMyActivityTab,
  ])

  const resolvedScheduleMeta: ScheduleMeta | undefined = (() => {
    const base = scheduleMeta ?? null
    if (!base && !forcedAssignedQuestionnaireIds && !forcedUserQuestionnaireIds) return undefined

    const origin =
      base?.origin ?? (forcedUserQuestionnaireIds ? "USER" : forcedAssignedQuestionnaireIds ? "UNIT" : undefined)

    const merged: ScheduleMeta = {
      ...(base ?? {}),
      origin,
      assignedQuestionnaireIds: origin === "UNIT" ? forcedAssignedQuestionnaireIds : undefined,
      userQuestionnaireIds: origin === "USER" ? forcedUserQuestionnaireIds : undefined,
    }

    return merged
  })()

  const providerValue: any = {
    listTab,
    changeTab,
    checkUser,
    state,
    items,
    loadingUserList,
    selectUser,
    changeActivitySection,
    selectUserToActivities,
    loadingActivities,
    filter: {
      filterListUser,
      filterOneUser,
      filterUser,
      filterMyActivity,
      filterDestinations,
    },
    service: {
      getAreaItem,
      searchUsersToAssign,
      getUsersItem,
      getDepartmentItem,
      usersMarkList,
      getStatusItem,
      getDetailInform,
      getStoreItem,
      change,
      getActivities,
      getDestinations,
      getMyTypes,
      datesItems,
      getDivisionItem,
      getRoleItem,
      getUserTypeItem,
      deleteActivity,
      deleteDestination,
      updateDestination,
      convertInUnity,
      getReportDestination,
      getByUuidDestination,
    },
    visibleUsersData,
    setVisibleUsersData,
    visibleUsersDataGeneralList,
    setVisibleUsersDataGeneralList,
    visibleUsersDataByUser,
    setVisibleUsersDataByUser,
    hiddenLocalConstant,
    setHiddenLocalConstant,
    scheduleMeta: resolvedScheduleMeta,
  }

  return <LogbookContext.Provider value={providerValue}>{children}</LogbookContext.Provider>
}
